import React, { useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import CTAButton from 'src/components/common/CTAButtonNew'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { selectCTAParams } from 'src/modules/reducers/params'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { createUrl } from 'src/modules/utils/createUrl'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import styled from 'styled-components'
import Image from '../Image'
import { body, heading, typographyMixin } from 'src/constants/marketing-ds/typography'
import { spacing } from 'src/constants/marketing-ds/spacing'
import { Checkmark } from 'src/components/icons/CheckMark'
import { CrossMark } from 'src/components/icons/CrossMark'
import { borderRadius, colors, mediaQuery } from 'src/constants/marketing-ds'

const PlanCardWithImage = ({
  promoImg,
  plan,
  length,
  title,
  subtitle,
  currency,
  price,
  discountedPrice,
  savingsNote,
  // listItems will need to be an array of objects of {string, boolean}
  listItems,
  offerCode,
  ctaUrl,
  ctaText,
  asterisk,
  footnote,
  onClick,
  btnColor,
  banner,
  bannerImage,
  networkLogos,
  showNetworks,
  cardClassName,
  lang = 'en',
  prePriceText,
  backgroundColor,
  imageOverlay = 'linear-gradient(195deg, rgba(4, 9, 17, .1) 20%, rgb(4, 9, 17) 72%)',
  titleLogo,
  bannerBgColor,
  footnoteInCard,
  fillImageSpace,
  showLengthText = true,
  networkLogosTop,
}) => {
  const parsedParams = useSelector(selectCTAParams)
  let ctaURL = createUrl(defaultCTAURI, parsedParams)
  ctaURL = replaceUrlParam(ctaURL, 'offer', offerCode)
  ctaURL = replaceUrlParam(ctaURL, 'comp', 'plan-card')
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  //PlanCardWithImage index.js
  const handleOnClick = () => {
    fireUIInteractionEvent(
      {
        element: offerCode,
        component: 'plan_card_cta_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  const footnoteInCardRef = useRef(null)
  const monthLang = showLengthText ? (lang === 'es' ? '/mes' : '/mo') : ''

  useEffect(() => {
    const matchHeights = () => {
      // Only match heights if screen is larger than mobile breakpoint
      if (window.innerWidth <= 576) {
        // Reset heights on mobile
        if (footnoteInCardRef.current) footnoteInCardRef.current.style.minHeight = 'auto'
        return
      }

      // Desktop behavior
      if (footnoteInCardRef.current) footnoteInCardRef.current.style.minHeight = 'auto'

      const footnotes = document.querySelectorAll('.planCard__footnote_in_card')
      let maxHeight = 0
      footnotes.forEach((footnote) => {
        const height = footnote.getBoundingClientRect().height
        maxHeight = Math.max(maxHeight, height)
      })

      footnotes.forEach((footnote) => {
        footnote.style.minHeight = `${maxHeight}px`
      })
    }

    // Initial match
    matchHeights()

    // Add resize listener
    window.addEventListener('resize', matchHeights)

    // Cleanup
    return () => window.removeEventListener('resize', matchHeights)
  }, [footnote])

  return (
    <StyledPlanCardWithImage
      className={`planCard-container ${cardClassName || ''}  ${networkLogosTop && networkLogosTop.length > 0 ? 'planCard-with-network-logos-top' : ''}`}
      backgroundColor={promoImg ? null : backgroundColor}
      imageOverlay={imageOverlay}
      hasBanner={!!(banner || bannerImage)}
      bannerBgColor={bannerBgColor}
    >
      <a
        className={`planCard ${promoImg || fillImageSpace ? 'planCard-with-image' : ''} `}
        href={ctaURL}
        onClick={handleOnClick}
      >
        {(banner || bannerImage) && (
          <div className={`planCard__banner  ${bannerImage ? 'planCard__banner--image' : ''}`}>
            {bannerImage ? <Image src={bannerImage} alt="Banner" /> : banner}
          </div>
        )}
        <div
          className={
            'planCard__image ' + (banner || bannerImage ? ' planCard__image-top-space' : '')
          }
        >
          {promoImg && <Image src={promoImg} alt={title} />}
        </div>
        <div className="planCard__content">
          {networkLogosTop && networkLogosTop.length > 0 && (
            <NetworkTopLogosContainer>
              {networkLogosTop.map(({ logo, alt }) => (
                <div key={alt} className="planCard__network-logo">
                  {logo ? (
                    <NetworkTopLogos className="network-top-logo" src={logo} alt={alt} />
                  ) : (
                    <div className="planCard__network-logo-text">{alt}</div>
                  )}
                </div>
              ))}
            </NetworkTopLogosContainer>
          )}
          <div className="planCard__header">
            <div className="planCard__title">
              {titleLogo ? (
                <Image src={titleLogo} alt={title} height={40} />
              ) : (
                <span>
                  {title} {length}
                </span>
              )}
            </div>
            {subtitle && <div className="planCard__subtitle">{subtitle}</div>}
            {networkLogos && networkLogos.length > 0 && (
              <div className="planCard__networks">
                {networkLogos.map(({ logo, alt, width, height }) => (
                  <div key={alt} className="planCard__network-logo">
                    {logo ? (
                      <StyledImg src={logo} alt={alt} customWidth={width} customHeight={height} />
                    ) : (
                      <div className="planCard__network-logo-text">{alt}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="planCard__divider"></div>
            <div className="planCard__price">
              {discountedPrice ? (
                <>
                  <span>
                    {currency}${discountedPrice}
                    {monthLang}
                    {asterisk && <span className="asterisk">*</span>}
                  </span>
                  <span className="faded-price">
                    {currency}${price}
                    {monthLang}
                  </span>
                </>
              ) : (
                <span>
                  {prePriceText} {currency}${price}
                  {monthLang}
                  {asterisk && <span className="asterisk">*</span>}
                </span>
              )}
              {savingsNote && (
                <div className="discount__note">
                  {typeof savingsNote === 'string' && <span>{savingsNote}</span>}
                </div>
              )}
            </div>
          </div>
          <PlanCardList className="planCard__list">
            {listItems?.map((item, index) => {
              if (item === null) return null
              const check =
                item?.available === undefined ? (
                  <span style={{ height: '20px' }}> </span>
                ) : item?.available ? (
                  <Icon type="check">
                    <Checkmark />
                  </Icon>
                ) : (
                  <Icon type="cross">
                    <CrossMark />
                  </Icon>
                )

              return (
                <ListItem key={index} className="list-item">
                  {check} {item.text}
                </ListItem>
              )
            })}
          </PlanCardList>
        </div>
        <div className="planCard_cta">
          <CTAButton link={ctaUrl} color={btnColor && '#ffffff'}>
            {ctaText}
          </CTAButton>
        </div>
        {footnoteInCard != null && (
          <div className="planCard__footnote_in_card" ref={footnoteInCardRef}>
            {footnoteInCard}
          </div>
        )}
      </a>
      {footnote && <div className="planCard__footnote">{footnote}</div>}
    </StyledPlanCardWithImage>
  )
}

export default PlanCardWithImage

const StyledImg = styled.img`
  max-height: ${(props) => `${props.customHeight}px`};
  max-width: ${(props) => `${props.customWidth}px`};
  @media (max-width: 576px) {
    max-height: ${(props) => `${props.customHeight * 0.9}px`};
    max-width: ${(props) => `${props.customWidth * 0.9}px`};
  }
`

const StyledPlanCardWithImage = styled.div`
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  flex: 1;
  align-self: center;
  ${mediaQuery.desktop} {
    align-self: stretch;
    min-width: 0; /* Allow flex items to shrink below their content size */
    width: 100%; /* Ensure cards stretch to fill available space */
  }

  .planCard {
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
    padding-top: ${spacing.xxxxl};
    padding-bottom: ${spacing.lg};
    border-radius: ${(props) =>
      props.hasBanner ? `0 0 ${borderRadius.md} ${borderRadius.md}` : borderRadius.md};
    box-sizing: border-box;
    background: ${colors.slate[10]};
    cursor: pointer;
    transition: transform 0.4s;
    &:hover {
      transform: scale(1.01);
    }

    &.planCard-with-image {
      padding-top: 132px;
    }

    .planCard__image {
      width: 100%;
      position: absolute;
      top: 0;
      z-index: 1;
      background: ${({ backgroundColor }) => backgroundColor || 'transparent'};
      border-radius: ${borderRadius.md} ${borderRadius.md} 0 0;
      min-height: 300px;
      max-height: 550px;
      overflow: hidden;
      &.planCard__image-top-space {
        border-radius: 0;
      }
      img {
        width: 100%;
      }
      &::after {
        background: ${({ imageOverlay }) => imageOverlay};
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 101%;
      }
    }
    .planCard__banner {
      z-index: 3;
      position: absolute;
      top: 0;
      width: 100%;
      text-align: center;
      background: ${({ bannerBgColor }) => bannerBgColor || colors.blue[80]};
      color: #fff;
      padding: ${spacing.xs} 0;
      ${typographyMixin(heading.xs)}
      box-sizing: border-box;
      transform: translateY(-100%);
      border-radius: ${borderRadius.md} ${borderRadius.md} 0 0;

      /* Image banner styling */
      img {
        width: 100%;
        height: auto;
        display: block;
        border-radius: ${borderRadius.md} ${borderRadius.md} 0 0;
      }

      /* When banner contains an image, remove text-specific styling */
      &.planCard__banner--image {
        padding: 0;
        background: transparent;
        color: inherit;
      }
    }

    .planCard__content {
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 2;
      padding: 0 ${spacing.md};
      flex: 1 1 auto;
      min-height: 0;
      ${mediaQuery.tablet} {
        padding: 0 ${spacing.xl};
      }

      .planCard__header {
        flex-shrink: 0;
        .planCard__title {
          ${typographyMixin(heading.lg)}
          // height: calc(${heading.lg.fontSize} * ${heading.lg.lineHeight});
          display: flex;
          align-items: flex-end;
          margin-bottom: ${spacing.xxxxs};
          text-shadow: -1px 1px 6px rgba(4, 9, 17, 0.7);
          ${mediaQuery.mobileMax} {
            height: calc(${heading.lg.fontSize} * ${heading.lg.lineHeight});
          }
        }
        .planCard__subtitle {
          ${typographyMixin(body.sm)}
        }
        .planCard__divider {
          width: 64px;
          height: 1px;
          background: #fff;
          margin: ${spacing.lg} 0;
        }
        .planCard__price {
          display: flex;
          align-items: center;
          gap: ${spacing.xxs};
          flex-wrap: wrap;
          ${typographyMixin(heading.md)}
          min-width: 0; /* Allow flex items to shrink */
          ${mediaQuery.desktop} {
            flex-wrap: nowrap; /* Prevent wrapping on desktop when there's space */
          }
        }
        .faded-price {
          ${typographyMixin(body.lg)}
          color: ${colors.slate[100]};
          text-decoration: line-through;
          -webkit-text-decoration-color: ${colors.red[120]};
          text-decoration-color: ${colors.red[120]};
        }
        .planCard__networks {
          display: flex;
          flex-direction: row;
          margin-top: ${spacing.lg};
          flex-wrap: wrap;
          gap: ${spacing.xxs};
          min-height: 48px;
        }
        .planCard__network-logo {
          display: flex;
          align-items: center;
          .planCard__network-logo-text {
            ${typographyMixin(body.sm)}
            white-space: nowrap;
          }
        }
        .discount__note {
          ${typographyMixin(heading.xs)}
          text-decoration: none;
          text-transform: none;
          color: ${colors.slate[10]};
          display: inline-block;
          background-color: ${colors.mint[120]};
          border-radius: ${borderRadius.xs};
          flex-shrink: 0; /* Prevent the discount note from shrinking */

          span {
            display: inline-block;
            padding: ${spacing.xxxs};
            white-space: nowrap;
          }
        }
      }
    }
    .planCard_cta {
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 2;
      padding: 0 11%;
      margin-top: auto;
      justify-content: flex-end;
    }
    .planCard__footnote_in_card {
      ${typographyMixin(body.xs)}
      text-align: center;
      color: ${colors.white};
      margin-top: ${spacing.sm};
      padding: 0 ${spacing.md};
      ${mediaQuery.tablet} {
        padding: 0 ${spacing.lg};
      }
    }
  }
  .planCard__footnote {
    ${typographyMixin(body.xs)}
    text-align: center;
    color: ${colors.white};
    min-height: 60px;
    margin-top: ${spacing.sm};
    padding: 0 ${spacing.sm};
  }

  &.planCard-with-network-logos-top {
    .planCard {
      padding-top: ${spacing.lg};
      ${mediaQuery.mobileMax} {
        padding-top: ${spacing.xxl};
      }
      .planCard__image {
        &:after {
          background: linear-gradient(160deg, rgba(4, 9, 17, 0.1) 10%, rgb(4, 9, 17) 44%);
        }
      }
    }
  }
`
const Icon = styled.span`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ type }) => (type === 'check' ? colors.mint[80] : colors.red[120])};
`

// List of features/benefits
const PlanCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}; // 16px gap from Figma
  margin: ${spacing.lg} 0 ${spacing.xl};
  min-height: 0;
  justify-content: flex-start;
`
// Individual list item
const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  ${typographyMixin(body.sm)}
  color: ${colors.white};
  gap: 0 6px;
  min-height: 24px;
`

const NetworkTopLogos = styled.img`
  height: 47px;
  width: 47px;

  ${mediaQuery.mobileMax} {
    height: 67px;
    width: 67px;
  }
`

const NetworkTopLogosContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${spacing.md};
  ${mediaQuery.mobileMax} {
    margin-bottom: ${spacing.lg};
  }
  .planCard__network-logo-text {
    font-size: 34px;
    margin: 0 10px;
  }

  .planCard__network-logo img {
    &:first-child {
      margin-left: 0;
    }
    margin-right: 4px;
  }
`
